import "./homeGroupAssessor.css";

import { useEffect, useState } from "react";

//import components
import TotalCompliance from "../../components/totalCompliance/TotalCompliance";
import ComplianceAssessment from "../../components/complianceAssessment/ComplianceAssessment";
import ComplianceCategory from "../../components/complianceCategory/ComplianceCategory";
import { RecordBox } from "../../components/recordBox/RecordBox";

import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { labelOfFramework } from "../../util/labelOf";
import { CountObl } from "../../components/countObl/CountObl";

const HomeGroupAssessorPage = () => {
  //get from store
  const user = useSelector(state => state.user);
  const userRole = useSelector(state => state.user.role);
  const organizations = useSelector(state => state.organizations.organizations);
  const organizationId = useSelector(
    state => state.organizations.organizationId
  );
  const obligations = useSelector(state => state.obligations.obligations);
  const counters = useSelector(state => state.obligations.obligationsCounters);
  const usersData = useSelector(state => state.organizations.usersData);
  const activeCompany =
    organizations?.find(org => org._id === organizationId) ?? {};

  const numberOfLessObligationsToShow = 2;

  const partiallyCompliantCount =
    counters?.countPartiallyCompliantObligations ?? 0;
  const compliantCount = counters?.countCompliantObligations ?? 0;
  const nonCompliantCount = counters?.countNonCompliantObligations ?? 0;
  const pendingCount = counters?.countPendingObligations ?? 0;
  const naCount = counters?.countNAObligations ?? 0;

  //utility functions
  function getCompliantPercentage(obligations = [], mainCategory) {
    // Filter obligations by the given main category
    const categoryObligations = obligations?.filter(o => {
      const inForceDate = new Date(o?.inForceDate);
      const now = new Date();
      return inForceDate <= now && o.mainCategory === mainCategory;
    });

    // If there are no obligations for this category, return 0
    if (categoryObligations?.length === 0) {
      return 0;
    }

    // Filter category obligations that are "Compliant"
    const compliantObligations = categoryObligations?.filter(
      obligation => obligation.assessment === "Compliant"
    );

    // Calculate percentage
    const percentage =
      (compliantObligations?.length / categoryObligations?.length) * 100;

    // Round the percentage to nearest integer
    const roundedPercentage = Math.round(percentage);

    return roundedPercentage;
  }

  //handlers
  // function handleClickOnDpo() {
  //   triggerNotification({
  //     message: "Successful Action", //was "Operation Successful",
  //     description: "Τhe action has been successfully completed." //was "The obligation has been assigned succefully and an email has been sent to the user."
  //   });
  // }

  // Safely map over obligations
  const uniqueMainCategories =
    obligations?.length > 0
      ? [...new Set(obligations.map(ob => ob.mainCategory))]
      : [];

  const progressData = uniqueMainCategories.map(category => ({
    value: getCompliantPercentage(obligations, category),
    name: labelOfFramework(category),
    code: category
  }));

  const sortedProgressData = [...progressData].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  //set closed to expire obligations as component state
  const [closeToExpire, setCloseToExpire] = useState([]);
  //set expired obligations as component state
  const [expired, setExpired] = useState([]);
  //set inforce obligations array as component state
  const [inforce, setInforce] = useState([]);
  const [expiredCounters, setExpiredCounters] = useState({
    partially: 0,
    non: 0,
    na: 0,
    pending: 0,
    assigned: 0,
    unassigned: 0
  });

  //fetch obligations from the database and store them in the state
  useEffect(() => {
    const closeToExpireArray = [];
    const expiredArray = [];
    const inForceArray = [];

    if (!isEmpty(obligations))
      obligations.forEach(o => {
        const dueDate = new Date(o.dueDate);
        const inForceDate = new Date(o.inForceDate);
        const now = new Date();
        const futureDate = new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000);

        if (o.dueDate && dueDate < now && o.assessment !== "Compliant") {
          // expired and not compliant go to EXPIRED
          expiredArray.push(o);
        } else if (o.dueDate && inForceDate <= now && dueDate < futureDate && dueDate >= now) {
          //close to expire but not yet expired go to CLOSE TO EXPIRE
          closeToExpireArray.push(o);
        } else if (o.dueDate && inForceDate <= now) {
          //compliant expired and all the rest go to WHAT'S NEW
          inForceArray.push(o);
        }
      });

    // Sort inForceArray based on createdAt in descending order
    inForceArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Sort closeToExpireArray based on dueDate in ascending order
    closeToExpireArray.sort(
      (a, b) => new Date(a.dueDate) - new Date(b.dueDate)
    );

    // Sort expiredArray based on dueDate in ascending order
    expiredArray.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

    const newCounters = expiredArray.reduce((acc, obl) => {
      let assessmentType = "";
      if (obl?.assessment === "Pending") assessmentType = "pending";
      else if (obl?.assessment === "na") assessmentType = "na";
      else if (obl?.assessment === "Non-Compliant") assessmentType = "non";
      else if (obl?.assessment === "Partially") assessmentType = "partially";
      if (assessmentType) acc[assessmentType] = (acc[assessmentType] || 0) + 1;
      if (obl?.isAssigned) acc["assigned"] = (acc["assigned"] || 0) + 1;
      else acc["unassigned"] = (acc["unassigned"] || 0) + 1;
      return acc;
    }, {});

    setExpiredCounters({ ...newCounters });

    setCloseToExpire(closeToExpireArray);
    setExpired(expiredArray);
    setInforce(inForceArray);
  }, [obligations]);

  const countCompliantTasks = itemList =>
    itemList
      ? itemList.filter(item => item.assessment === "Compliant").length
      : 0;

  //Calculate by Assessment widget data
  // Total obligations count that are in force
  const totalObligations = !isEmpty(obligations)
    ? obligations?.filter(o => {
        const inForceDate = new Date(o?.inForceDate);
        const now = new Date();
        return inForceDate <= now;
      })?.length - counters?.countNAObligations
    : 0;

  // Calculate the percentage for each type of obligation
  const partiallyCompliantPercent = totalObligations
    ? (partiallyCompliantCount / totalObligations) * 100
    : 0;
  const compliantPercent = totalObligations
    ? (compliantCount / totalObligations) * 100
    : 0;
  const nonCompliantPercent = totalObligations
    ? (nonCompliantCount / totalObligations) * 100
    : 0;
  const pendingPercent = totalObligations
    ? (pendingCount / totalObligations) * 100
    : 0;
  const naPercent = totalObligations ? (naCount / totalObligations) * 100 : 0;

  return (
    <div id="home-gca-page" className="home">
      <div className="homeContainer" id="home-gce-container">
        {/* <h1 className="homeTitle">This is the home page</h1>
          <RecordSimple /> */}
        <div className="leftArea" id="home-gce-left-area">
          <div id="compliance-category">
            <ComplianceCategory data={sortedProgressData}></ComplianceCategory>
          </div>
          {(!isEmpty(usersData) || userRole === "groupComplianceAssessor") && (
            <>
              <RecordBox
                title="Close to Expire"
                id="close-to-expire"
                obligations={closeToExpire}
                user={user}
                numberOfLessObligationsToShow={numberOfLessObligationsToShow}
                usersData={usersData}
                // handleClickOnDpo={handleClickOnDpo}
              />
              <RecordBox
                title="Expired"
                id="expired"
                obligations={expired}
                user={user}
                numberOfLessObligationsToShow={numberOfLessObligationsToShow}
                usersData={usersData}
                // handleClickOnDpo={handleClickOnDpo}
              />
              <RecordBox
                title="What's New"
                id="what-is-new"
                obligations={inforce}
                user={user}
                numberOfLessObligationsToShow={numberOfLessObligationsToShow}
                usersData={usersData}
                // handleClickOnDpo={handleClickOnDpo}
              />
            </>
          )}
        </div>
        <div className="rightArea" id="home-gce-right-area" flex={1}>
          <TotalCompliance
            id="home-gce-total-compliance"
            score={
              totalObligations && !isEmpty(obligations)
                ? (
                    countCompliantTasks(
                      obligations?.filter(o => {
                        const inForceDate = new Date(o?.inForceDate);
                        const now = new Date();
                        return inForceDate <= now;
                      })
                    ) / totalObligations
                  ).toFixed(2)
                : 0
            }
            org={activeCompany?.customerName}
          />
          <CountObl
            id="home-gce-count-obligations"
            totalObligations={totalObligations || 0}
            compliant={counters?.countCompliantObligations ?? 0}
            partiallyCompliant={
              counters?.countPartiallyCompliantObligations ?? 0
            }
            nonCompliant={counters?.countNonCompliantObligations ?? 0}
            na={counters?.countNAObligations ?? 0}
            pending={counters?.countPendingObligations ?? 0}
            open={totalObligations ? totalObligations - (counters?.countCompliantObligations ?? 0) : 0}
            assigned={counters?.countAssignedObligations ?? 0}
            unassigned={counters?.countUnassignedObligations ?? 0}
            expiredCounters={expiredCounters}
          />

          {/* <RecordSimple />
          <RecordSimple />
          <RecordSimple />
          <LinkButton text="Go to my Tasklist" /> */}
          <ComplianceAssessment
            id="home-gce-compliance-assessment"
            data={[
              naPercent,
              pendingPercent,
              nonCompliantPercent,
              partiallyCompliantPercent,
              compliantPercent
            ]}
          ></ComplianceAssessment>
        </div>
      </div>
    </div>
  );
};

export default HomeGroupAssessorPage;
